import { useCallback, useEffect, useRef, useState } from "react";
import { brandsApi } from "../api/brandsApi";
import { useToast } from "../context/ToastContext";
import { LoadingState, ErrorState } from "../components/ApiState";
import EmptyState from "../components/EmptyState";
import { Stat, fmtDateTime } from "../utils/periodShared";

const FIELDS = [["posts", "Posts"], ["followers", "Followers"], ["following", "Following"]];

function Avatar({ brand }) {
  const [broken, setBroken] = useState(false);
  return brand.profile_picture_url && !broken
    ? <img className="metrics-avatar" src={brand.profile_picture_url} alt="" onError={() => setBroken(true)} />
    : <div className="metrics-avatar metrics-avatar-empty">{brand.username?.[0]?.toUpperCase()}</div>;
}

/** All Brands: every connected Instagram account (brand = account) with Posts, Followers and Following. */
export default function Brands() {
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const autoDone = useRef(false);

  const refresh = useCallback(async (quiet) => {
    setRefreshing(true);
    try {
      const r = await brandsApi.refresh();
      setData(r);
      if (!quiet) showToast(r.failed?.length ? `Updated, but ${r.failed.length} brand${r.failed.length === 1 ? "" : "s"} could not be read from Meta.` : "Brand statistics updated.");
    } catch (e) { if (!quiet) showToast(e.message || "Could not refresh."); }
    finally { setRefreshing(false); }
  }, [showToast]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await brandsApi.list();
      setData(r);
      if (r.stale && !autoDone.current) { autoDone.current = true; refresh(true); } // values older than 2 hours: update them in the background
    } catch (e) { setError(e.message || "Could not load brands."); }
  }, [refresh]);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="view-header">
        <div>
          <h2>All Brands</h2>
          <p>Every connected Instagram account, with its current number of posts, followers and following.</p>
        </div>
        <button className="btn btn-secondary" onClick={() => refresh(false)} disabled={refreshing}>{refreshing ? "Refreshing…" : "Refresh"}</button>
      </div>

      {error ? <ErrorState message={error} onRetry={load} /> : !data ? <LoadingState label="Loading brands..." /> : data.results.length === 0 ? (
        <EmptyState title="No brands yet." desc="Connect an Instagram account under Instagram Accounts and it will appear here." />
      ) : (
        <div className="brands-list">
          {data.results.map((b) => (
            <section className="brand-card" key={b.id}>
              <div className="metrics-profile-head">
                <Avatar brand={b} />
                <div>
                  <div className="metrics-username">@{b.username}</div>
                  {b.display_name && <div className="metrics-display">{b.display_name}</div>}
                </div>
              </div>
              <div className="metrics-ig-stats">
                {FIELDS.map(([k, label]) => (
                  <div key={k} className="metrics-ig-stat">
                    <div className="metrics-ig-value"><Stat value={b[k]} /></div>
                    <div className="metrics-ig-label">{label}</div>
                  </div>
                ))}
              </div>
              <div className="metrics-caption">{b.updated_at ? `Current values · updated ${fmtDateTime(b.updated_at, data.timezone)}.` : "Not fetched from Meta yet. Press Refresh."}</div>
            </section>
          ))}
        </div>
      )}
    </>
  );
}
